import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { dubaiDate } from '../../../utils/dubaiTime';
import './viewTeamMember.css';

// 0 = Sunday .. 6 = Saturday, matching Date.getDay() and the stored column.
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const dayOffLabel = (v) => (v === null || v === undefined || v === '' ? '' : (DAY_NAMES[Number(v)] || ''));

const DOC_TYPES = [
    { type: 'personal_photo', label: 'Personal photo' },
    { type: 'passport', label: 'Passport' },
    { type: 'last_visa', label: 'Visa' },
    { type: 'labour_card', label: 'Labour card' },
    { type: 'eid_front', label: 'Emirates ID (front)' },
    { type: 'eid_back', label: 'Emirates ID (back)' },
    { type: 'health_insurance_card', label: 'Health insurance card' },
    { type: 'driving_license_front', label: 'Driving license (front)', driversOnly: true },
    { type: 'driving_license_back', label: 'Driving license (back)', driversOnly: true },
];

// Asia/Dubai render (item 8) — same dd/mm/yyyy visual as before.
const formatDate = (value) => (value ? dubaiDate(value) || null : null);

const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    if (Number.isNaN(num)) return null;
    return `AED ${num.toLocaleString('en-GB', { maximumFractionDigits: 2 })}`;
};

// Raw numeric string for clipboard so pasting into MOHRE/bank forms works
// without the "AED " prefix (dates, by contrast, copy exactly as displayed).
const moneyCopyValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    return Number.isNaN(num) ? null : String(num);
};

// The documents payload may arrive as staff_documents rows or as a
// { doc_type: url } map depending on the endpoint version; normalize both.
const normalizeDocuments = (data) => {
    const raw = data && data.documents;
    if (Array.isArray(raw)) {
        return raw.reduce((acc, doc) => {
            if (doc && doc.doc_type) acc[doc.doc_type] = doc.file_url || null;
            return acc;
        }, {});
    }
    if (raw && typeof raw === 'object') {
        return Object.keys(raw).reduce((acc, key) => {
            const entry = raw[key];
            acc[key] = typeof entry === 'string' ? entry : (entry && entry.file_url) || null;
            return acc;
        }, {});
    }
    return {};
};

// Read-only dossier modal. Owns its overlay/panel (vtm-*) so it never
// collides with the add/edit modal classes in team.css.
function ViewTeamMember({ memberId, onClose, onEdit }) {
    const [member, setMember] = useState(null);
    const [error, setError] = useState(null);
    const [copiedKey, setCopiedKey] = useState(null);

    // Same protected-role convention as EditTeamMember: manager AND supervisor
    // dossiers are only viewable by managers (the backend 404s managers; the
    // supervisor gate is a frontend convention this modal must share).
    const userRole = String(localStorage.getItem('userRole') || '').toLowerCase();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const response = await axios.get(`/api/team/${memberId}`);
                if (cancelled) return;
                const roleName = String(response.data?.role_name || '').toLowerCase();
                if ((roleName === 'manager' || roleName === 'supervisor') && userRole !== 'manager') {
                    setError('Team member not found.');
                    return;
                }
                setMember(response.data);
            } catch (err) {
                console.error('Error fetching team member details:', err);
                if (!cancelled) {
                    setError(err.response?.status === 404
                        ? 'Team member not found.'
                        : 'Error fetching team member details.');
                }
            }
        })();
        return () => { cancelled = true; };
    }, [memberId, userRole]);

    const [downloadingDoc, setDownloadingDoc] = useState(null);

    // The `download` attribute is ignored on cross-origin CDN links, so a
    // proper download means fetching the blob and saving it locally with a
    // readable filename ("First_Last-Passport photo.jpeg").
    const handleDownloadDocument = async (docType, label) => {
        const url = documents[docType];
        if (!url || downloadingDoc) return;
        setDownloadingDoc(docType);
        try {
            const res = await fetch(url, { credentials: 'omit' });
            if (!res.ok) throw new Error(`fetch failed (${res.status})`);
            const blob = await res.blob();
            const ext = (url.split('.').pop() || 'file').split('?')[0];
            const who = [member?.first_name, member?.last_name].filter(Boolean).join('_') || 'member';
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = `${who}-${label}.${ext}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);
        } catch (err) {
            console.error('Document download failed:', err);
            window.alert('Could not download the file — try Open instead.');
        }
        setDownloadingDoc(null);
    };

    const handleCopy = (key, value) => {
        if (!value) return;
        navigator.clipboard.writeText(value)
            .then(() => {
                setCopiedKey(key);
                setTimeout(() => setCopiedKey((prev) => (prev === key ? null : prev)), 1200);
            })
            .catch((err) => console.error('Failed to copy:', err));
    };

    // Icon-only copy button (24px hit area). Styling is fully explicit in
    // viewTeamMember.css because App.css has a global button rule.
    const renderCopyButton = (key, value) => (
        <button
            type="button"
            className={`vtm-copy${copiedKey === key ? ' copied' : ''}`}
            onClick={() => handleCopy(key, value)}
            title={copiedKey === key ? 'Copied' : 'Copy'}
            aria-label={copiedKey === key ? 'Copied' : `Copy ${key.replace(/_/g, ' ')}`}
        >
            <FontAwesomeIcon icon={copiedKey === key ? faCheck : faCopy} />
        </button>
    );

    // Reusable dossier row: label + value + copy affordance. Empty values
    // render an em dash with no copy button. `copyValue` overrides what goes
    // to the clipboard (e.g. raw number for formatted money).
    const renderRow = (label, key, value, copyValue) => (
        <div className="vtm-row">
            <span className="vtm-label">{label}</span>
            {value ? (
                <span className="vtm-value-wrap">
                    <span className="vtm-value">{value}</span>
                    {renderCopyButton(key, copyValue !== undefined ? copyValue : value)}
                </span>
            ) : (
                <span className="vtm-empty">—</span>
            )}
        </div>
    );

    const renderContactRow = (label, field, asWhatsAppLink = false) => {
        const value = member[field];
        return (
            <div className="vtm-row">
                <span className={`vtm-label${asWhatsAppLink ? ' vtm-label-whatsapp' : ''}`}>{label}</span>
                {value ? (
                    <span className="vtm-value-wrap">
                        {asWhatsAppLink ? (
                            <a
                                className="vtm-value vtm-whatsapp-link"
                                href={`https://wa.me/${value.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noreferrer"
                                title="Open WhatsApp chat"
                            >
                                {value}
                            </a>
                        ) : (
                            <span className="vtm-value">{value}</span>
                        )}
                        {renderCopyButton(field, value)}
                    </span>
                ) : (
                    <span className="vtm-empty">—</span>
                )}
            </div>
        );
    };

    const renderStaffTag = () => {
        if (member.is_trial) return <span className="vtm-pill vtm-pill-trial">Trial staff</span>;
        if (member.does_have_id_from_company) return <span className="vtm-pill vtm-pill-company-id">Company ID</span>;
        if (member.does_have_id) return <span className="vtm-pill vtm-pill-has-id">Has ID</span>;
        return null;
    };

    const fullName = member
        ? [member.first_name, member.middle_name, member.last_name].filter(Boolean).join(' ')
        : 'Team Member';

    const roleName = member ? String(member.role_name || '') : '';
    const roleLabel = roleName ? roleName.charAt(0).toUpperCase() + roleName.slice(1).toLowerCase() : '';
    const archived = member ? member.is_active === false : false;
    const isDriver = roleName.toLowerCase() === 'driver';

    // Backend omits these keys entirely for non-managers, so key presence
    // (not truthiness) decides whether the section exists.
    const hasManagerFields = member
        && ('mohre_salary' in member || 'conditional_bonus' in member
            || 'contractual_day_off' in member);

    const documents = member ? normalizeDocuments(member) : {};
    const docRows = DOC_TYPES.filter((doc) => !doc.driversOnly || isDriver);

    return (
        <div className="vtm-overlay">
            <div className="vtm-panel">
                <div className="vtm-header">
                    <div className="vtm-header-identity">
                        <div className="vtm-header-name">
                            <h3>{fullName}</h3>
                            {member && renderCopyButton('full_name', fullName)}
                        </div>
                        {member && (
                            <div className="vtm-pills">
                                {roleLabel && <span className="vtm-pill vtm-pill-role">{roleLabel}</span>}
                                <span className={`vtm-pill ${archived ? 'vtm-pill-archived' : 'vtm-pill-active'}`}>
                                    {archived ? 'Archived' : 'Active'}
                                </span>
                                {renderStaffTag()}
                            </div>
                        )}
                    </div>
                    <button type="button" className="vtm-close" onClick={onClose} title="Close">×</button>
                </div>

                <div className="vtm-body">
                    {error && <p className="vtm-error">{error}</p>}
                    {!error && !member && <p className="vtm-loading">Loading team member...</p>}

                    {member && (
                        <>
                            <section className="vtm-section">
                                <div className="vtm-section-title-row">
                                    <h4>Name</h4>
                                </div>
                                <div className="vtm-rows vtm-rows-grid">
                                    {renderRow('First name', 'first_name', member.first_name)}
                                    {renderRow('Middle name', 'middle_name', member.middle_name)}
                                    {renderRow('Last name', 'last_name', member.last_name)}
                                </div>
                            </section>

                            <section className="vtm-section">
                                <div className="vtm-section-title-row">
                                    <h4>Contact</h4>
                                </div>
                                <div className="vtm-rows">
                                    {renderContactRow('WhatsApp', 'phone_number', true)}
                                    {renderContactRow('Calling', 'calling_number')}
                                    {renderContactRow('Email', 'email')}
                                </div>
                            </section>

                            <section className="vtm-section">
                                <div className="vtm-section-title-row">
                                    <h4>Identity &amp; permits</h4>
                                </div>
                                <div className="vtm-rows vtm-rows-grid">
                                    {renderRow('Date of birth', 'date_of_birth', formatDate(member.date_of_birth))}
                                    {renderRow('Place of birth', 'place_of_birth', member.place_of_birth)}
                                    {renderRow('Passport number', 'passport_number', member.passport_number)}
                                    {renderRow('Passport issue date', 'passport_issue_date', formatDate(member.passport_issue_date))}
                                    {renderRow('Passport expiry date', 'passport_expiry_date', formatDate(member.passport_expiry_date))}
                                    {renderRow('Nationality', 'passport_issue_country', member.passport_issue_country)}
                                    {renderRow('Passport issue place', 'passport_issue_place', member.passport_issue_place)}
                                    {renderRow('Unified number', 'unified_number', member.unified_number)}
                                    {renderRow('Emirates ID', 'eid', member.eid || member.staff_eid)}
                                    {renderRow('EID expiry date', 'eid_expiry_date', formatDate(member.eid_expiry_date))}
                                    {renderRow('MOHRE person code', 'mohre_person_code', member.mohre_person_code)}
                                    {renderRow('Work permit number', 'work_permit_number', member.work_permit_number)}
                                    {renderRow('Work permit issue date', 'work_permit_issue_date', formatDate(member.work_permit_issue_date))}
                                    {renderRow('Work permit expiry date', 'work_permit_expiry_date', formatDate(member.work_permit_expiry_date))}
                                    {renderRow('Health insurance expiry', 'health_insurance_expiry', formatDate(member.health_insurance_expiry))}
                                </div>
                            </section>

                            <section className="vtm-section">
                                <div className="vtm-section-title-row">
                                    <h4>Documents</h4>
                                </div>
                                <div className="vtm-rows">
                                    {docRows.map((doc) => (
                                        <div key={doc.type} className="vtm-row">
                                            <span className="vtm-label vtm-label-doc">{doc.label}</span>
                                            {documents[doc.type] ? (
                                                <span className="vtm-doc-actions">
                                                    <a
                                                        className="vtm-doc-link"
                                                        href={documents[doc.type]}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        Open
                                                    </a>
                                                    <button
                                                        type="button"
                                                        className="vtm-doc-link vtm-doc-download"
                                                        onClick={() => handleDownloadDocument(doc.type, doc.label)}
                                                        disabled={downloadingDoc === doc.type}
                                                    >
                                                        {downloadingDoc === doc.type ? 'Downloading…' : 'Download'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`vtm-doc-link vtm-doc-download${copiedKey === `doc:${doc.type}` ? ' vtm-doc-copied' : ''}`}
                                                        onClick={() => handleCopy(`doc:${doc.type}`, documents[doc.type])}
                                                    >
                                                        {copiedKey === `doc:${doc.type}` ? 'Copied ✓' : 'Copy link'}
                                                    </button>
                                                </span>
                                            ) : (
                                                <span className="vtm-empty">Not uploaded</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </section>

                            {hasManagerFields && (
                                <section className="vtm-section">
                                    <div className="vtm-section-title-row">
                                        <h4>Contract</h4>
                                        <span className="vtm-manager-pill">Manager only</span>
                                    </div>
                                    <div className="vtm-rows">
                                        {renderRow('MOHRE Salary', 'mohre_salary', formatMoney(member.mohre_salary), moneyCopyValue(member.mohre_salary))}
                                        {renderRow('Conditional Bonus', 'conditional_bonus', formatMoney(member.conditional_bonus), moneyCopyValue(member.conditional_bonus))}
                                        {/* The day the contract gives off. Hours are flexible in
                                            practice — this is what leave and holiday entitlement
                                            are worked out against. */}
                                        {renderRow(
                                            'Contractual Day Off',
                                            'contractual_day_off',
                                            dayOffLabel(member.contractual_day_off),
                                            dayOffLabel(member.contractual_day_off)
                                        )}
                                    </div>
                                </section>
                            )}
                        </>
                    )}
                </div>

                <div className="vtm-footer">
                    <button type="button" className="vtm-close-button" onClick={onClose}>Close</button>
                    {onEdit && (
                        <button type="button" className="vtm-edit-button" onClick={onEdit}>Edit</button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ViewTeamMember;
